<template>
  <div :style="CSS_VARS" class="ecs-avatar" :class="{ ringing: hasIsRingingClass }">
    <v-avatar :size="size" :color="color">
      <img v-if="avatar" :src="avatar" class="ecs-avatar-image" alt="Avatar" />
      <span v-else class="white--text">
        {{ initials }}
      </span>
    </v-avatar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import EcsState from '@/components/ecs-agent-state/EcsState';
import { CONTACT_TYPE } from '@/constants/contactConstants';

export default {
  name: 'EcsAvatar',

  props: {
    contact: {
      type: Object,
      default: null
    },
    size: {
      type: Number,
      default: 40
    },
    contactId: {
      type: String,
      default: ''
    },
    agentId: {
      type: String,
      default: ''
    },
    alt: {
      type: String,
      default: ''
    },
    borderWidth: {
      type: Number,
      default: 0
    },
    backgroundColor: {
      type: String,
      default: null
    },
    // Disable animation
    noAnimation: {
      type: Boolean,
      default: false
    },
    stateful: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      color: 'var(--v-accent-base)',
      statusColors: {
        away: '#EDC018',
        busy: '#e63946',
        available: '#008F11',
        ringing: '#00CC00',
        offline: '#999999'
      }
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),
    ...mapGetters('CONTACTS', ['getAvailabilityState']),
    ...mapGetters('AVATARS', ['getAgentAvatar', 'getContactAvatar']),

    availabilityState() {
      const { agentId, contactId } = this.requestId;
      return this.stateful ? new EcsState(this.getAvailabilityState(agentId || contactId)) : null;
    },

    stateName() {
      if (!this.stateful) {
        return;
      }

      let result = 'Offline';

      if (this.availabilityState.isRinging) {
        result = 'Ringing';
      } else if (this.availabilityState.isReady) {
        result = 'Available';
      } else if (
        this.availabilityState.isBusy ||
        this.availabilityState.isBackoffice ||
        this.availabilityState.isOutgoing
      ) {
        result = 'Busy';
      } else if (this.availabilityState.isBreak) {
        result = 'Away';
      }

      return result.toLowerCase();
    },

    borderColor() {
      return this.stateful ? this.statusColors[this.stateName] : 'transparent';
    },

    initials() {
      const identifier = this.alt || (this.contact && this.contact.name) || '';
      if (!identifier) return null;

      let initials = identifier.replace(/[^a-zA-Z0-9- ]/g, '').match(/\b\w/g);
      initials = (initials || []).join('');
      initials = initials.slice(0, 2);

      if (initials.length === 1 && identifier.length > 1) {
        initials += identifier[1];
      }

      return initials.toUpperCase();
    },

    requestId() {
      let agentId = this.agentId;
      let contactId = this.contactId;

      if (this.contact) {
        if (![CONTACT_TYPE.crm, CONTACT_TYPE.businessProcess].includes(this.contact.contactType)) {
          agentId = this.contact.id;
        } else {
          contactId = this.contact.id;
        }
      }

      return { agentId, contactId };
    },

    avatar() {
      if (this.requestId.agentId) {
        return this.getAgentAvatar(this.requestId.agentId);
      } else if (this.requestId.contactId) {
        return this.getContactAvatar(this.requestId.contactId);
      } else {
        return null;
      }
    },

    CSS_VARS() {
      const borderWidth = this.stateful ? 2 : this.borderWidth;
      return {
        '--size': `${this.size + borderWidth * 2}px`,
        '--border-width': `${borderWidth}px`,
        '--font-size': this.size < 20 ? '8px' : `${this.size / 2.7}px`,
        '--border-color': this.borderColor || 'transparent'
      };
    },

    hasIsRingingClass() {
      return this.stateful && this.availabilityState.isRinging;
    }
  },

  created() {
    if (this.requestId.contactId) {
      return this.registerAvatarConsumer({
        contactId: this.requestId.contactId,
        manualRequest: this.manualRequest
      });
    }

    if (this.requestId.agentId) {
      return this.registerAvatarConsumer({
        agentId: this.requestId.agentId,
        manualRequest: this.manualRequest
      });
    }
  },

  beforeDestroy() {
    if (this.requestId.agentId || this.requestId.contactId) {
      this.unregisterAvatarConsumer({ agentId: this.requestId.agentId, contactId: this.requestId.contactId });
    }
  },

  methods: {
    ...mapActions('AVATARS', ['registerAvatarConsumer', 'unregisterAvatarConsumer'])
  }
};
</script>

<style scoped>
.ecs-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--size);
  height: var(--size);
  border: var(--border-width);
  border-style: solid;
  border-radius: 50%;
  border-color: var(--border-color);
  font-size: var(--font-size);
}

.avatar-enter-active {
  animation: scale-in 250ms;
}

.avatar-leave-active {
  animation: scale-in 250ms reverse;
}

/* CSS Animation Keyframes */
@keyframes scale-in {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.ringing {
  width: var(--size);
  height: var(--size);
  border-radius: 50%;
  animation: ringing-pulse 1.5s infinite;
}

@keyframes ringing-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 250, 0, 0.4);
  }
  100% {
    box-shadow: 0 0 0 15px rgba(0, 250, 0, 0);
  }
}
</style>
